/**
 * HTTP API contract between the web app (ApiBackend) and apps/server.
 * Spec: docs/GAME_DESIGN.md § 7. The server implements exactly these routes,
 * shapes and semantics; OfflineDemoBackend mirrors them locally.
 *
 * Conventions
 * - JSON bodies, UTF-8. Timestamps are ISO 8601 strings (UTC).
 * - Auth: `Authorization: Bearer <playerId>.<secret>` on every route except
 *   `GET /api/health`, `POST /api/players/anonymous`, `GET /api/schools` and
 *   `GET /api/geo/cities`. `GET /api/leaderboard` accepts it optionally (it is
 *   needed for the caller's own row and for scopes that default to the
 *   caller's location); there a missing, malformed or unknown token means
 *   "anonymous caller", never 401. On the required-auth routes a missing or
 *   unknown token is 401 `unauthorized` — the client then registers a new
 *   anonymous player and retries once. The server stores sha256(secret) only.
 * - Errors: any non-2xx response has the body `ApiErrorBody` and the status
 *   listed with each code in API_ERROR_STATUS.
 * - PlayerProfile / LeaderboardEntry / SchoolOption strings are never null:
 *   unknown text fields are "" (only `grade` and `schoolId` may be null).
 * - Every response body is validated by the client (./decode.ts); a 2xx body
 *   of the wrong shape surfaces as BackendError('invalid-response').
 * - Location fields: `country` is ISO 3166-1 alpha-2 ("ZA"); for South Africa
 *   `province` is the ISO 3166-2 code without prefix ("GP", "WC", "EC", "NC",
 *   "FS", "KZN", "NW", "MP", "LP"), elsewhere free text; `city` is free text
 *   compared case-insensitively.
 */
import type { GameEvent, Locale } from '@dropstack/core';
import type {
  LeaderboardMetric,
  LeaderboardPage,
  LeaderboardPeriod,
  LeaderboardQuery,
  LeaderboardScope,
  PlayerProfile,
  ProfileInput,
  SchoolOption,
  SubmitResult,
} from '../../contracts.ts';

export type HttpMethod = 'GET' | 'POST' | 'PATCH';

export interface RouteDef {
  readonly method: HttpMethod;
  /** Fastify-style path; `:id` is a path parameter. */
  readonly path: string;
}

/** Every API route. Use routePath() to fill path parameters on the client. */
export const API_ROUTES = {
  /** → HealthResponse. Unauthenticated. Used by createBackend() to pick server vs offline demo. */
  health: { method: 'GET', path: '/api/health' },
  /** CreateAnonymousPlayerRequest → CreateAnonymousPlayerResponse (201). Unauthenticated. */
  createAnonymousPlayer: { method: 'POST', path: '/api/players/anonymous' },
  /** → GetMeResponse. */
  getMe: { method: 'GET', path: '/api/players/me' },
  /** UpdateMeRequest (= ProfileInput) → UpdateMeResponse. Completes onboarding. */
  updateMe: { method: 'PATCH', path: '/api/players/me' },
  /** SchoolsQuery (query string) → SchoolsResponse. */
  searchSchools: { method: 'GET', path: '/api/schools' },
  /** CitiesQuery (query string) → CitiesResponse. */
  suggestCities: { method: 'GET', path: '/api/geo/cities' },
  /** CreateSessionRequest → CreateSessionResponse (201). */
  createSession: { method: 'POST', path: '/api/sessions' },
  /** SubmitRunRequest → SubmitRunResponse. `:id` = sessionId. */
  submitRun: { method: 'POST', path: '/api/sessions/:id/submit' },
  /** LeaderboardSearch (query string) → LeaderboardResponse. Auth optional. */
  leaderboard: { method: 'GET', path: '/api/leaderboard' },
} as const satisfies Record<string, RouteDef>;

export type RouteName = keyof typeof API_ROUTES;

/** Fill `:param` placeholders (URI-encoded): routePath(API_ROUTES.submitRun, { id }) → "/api/sessions/abc/submit". */
export function routePath(route: RouteDef, params: Record<string, string> = {}): string {
  return route.path.replace(/:([A-Za-z_]\w*)/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`routePath: missing :${name} for ${route.path}`);
    return encodeURIComponent(value);
  });
}

/** Authorization header value for a player. */
export function bearerToken(playerId: string, secret: string): string {
  return `Bearer ${playerId}.${secret}`;
}

// ───────────── Errors ─────────────

/**
 * Error codes and their HTTP status. `invalid-profile` carries field problems
 * in `details`. Run verdicts (rejected / flagged runs) are NOT errors: submit
 * answers 200 with `accepted: false` and a `reason`.
 */
export const API_ERROR_STATUS = {
  'bad-request': 400,
  'invalid-profile': 400,
  unauthorized: 401,
  'not-found': 404,
  'session-unknown': 404,
  'session-used': 409,
  'session-expired': 410,
  'rate-limited': 429,
  internal: 500,
  unavailable: 503,
} as const;

export type ApiErrorCode = keyof typeof API_ERROR_STATUS;

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    /** Developer-facing English message; the UI localises by `code`. */
    message: string;
    /** For `invalid-profile`: one entry per invalid field. */
    details?: ProfileFieldProblem[];
  };
}

/** A field-level validation problem (shared by client- and server-side profile validation). */
export interface ProfileFieldProblem {
  field: keyof ProfileInput;
  /** i18n-friendly code, e.g. "too-short", "not-allowed", "unknown-province". */
  reason: string;
}

// ───────────── Health ─────────────

export interface HealthResponse {
  ok: boolean;
  /**
   * Server's core RULES_VERSION. The server would refuse a run played under
   * other rules, so the client compares every session ticket's rulesVersion
   * with its own (screens/model/session.ts § startTicket): on a mismatch it
   * plays an unranked offline run instead and offers a reload.
   */
  rulesVersion: number;
  /** Server time, ISO 8601. */
  time: string;
}

// ───────────── Players ─────────────

export interface CreateAnonymousPlayerRequest {
  locale: Locale;
}

/**
 * The new player's credentials. `secret` is shown exactly once; the client
 * stores `{ playerId, secret }` (localStorage key "dropstack.auth"). The profile
 * starts with onboarded = false, leaderboardOptIn = false, country "ZA" and
 * empty name/school/location.
 */
export interface CreateAnonymousPlayerResponse {
  playerId: string;
  secret: string;
  profile: PlayerProfile;
}

export interface GetMeResponse {
  profile: PlayerProfile;
  /** Rating persisted after the last accepted run (warm-start source); null before the first. */
  savedRating: number | null;
}

/**
 * Onboarding / profile edit. Server validation (the client runs the same rules
 * in services/profileValidation.ts): displayName 3–20 chars, letters/digits/
 * single spaces, blocklist; grade integer 1–12; country a 2-letter code;
 * province a valid SA code or name when country is "ZA" (stored as the code),
 * free text up to 60 chars otherwise (may be empty); city 1–60 chars;
 * schoolName 2–80 chars. School: a known schoolId links that school (its
 * stored name wins); a null/absent/unknown schoolId links the school with the
 * same name in the same city and country (case/accent-insensitive), creating it
 * if there is none. Success sets onboarded = true.
 */
export type UpdateMeRequest = ProfileInput;

export interface UpdateMeResponse {
  profile: PlayerProfile;
}

/** Limits shared by client and server validation. */
export const PROFILE_LIMITS = {
  gradeMin: 1,
  gradeMax: 12,
  schoolNameMin: 2,
  schoolNameMax: 80,
  cityMax: 60,
  provinceMax: 60,
} as const;

// ───────────── Schools & cities ─────────────

/** Query string of GET /api/schools. All optional; `q` matches the name (case/diacritic-insensitive). */
export interface SchoolsQuery {
  q?: string;
  country?: string;
  province?: string;
  city?: string;
}

/** At most SCHOOL_RESULTS_MAX schools, prefix matches first, then alphabetical. */
export interface SchoolsResponse {
  schools: SchoolOption[];
}

export const SCHOOL_RESULTS_MAX = 20;

/** Query string of GET /api/geo/cities. */
export interface CitiesQuery {
  country: string;
  province?: string;
  q?: string;
}

/** Suggested cities (known towns plus cities of registered schools); [] outside South Africa means free text. */
export interface CitiesResponse {
  cities: string[];
}

export const CITY_RESULTS_MAX = 12;

// ───────────── Sessions ─────────────

/**
 * The server chooses seed and ratings — the client can't pick its level. The
 * only choice is the pace: `relaxed: true` asks for a "More time" run (every
 * block falls at RELAXED_PACE_SCALE × the standard time). The server stores
 * it with the session, replays at that pace, and never ranks the run. Clients
 * send `{}` for a standard run (older servers accept only the empty object).
 */
export interface CreateSessionRequest {
  relaxed?: boolean;
}

/**
 * A signed, single-use run ticket (3 h TTL). startRating = warm start from the
 * saved rating (0 for a first run); referenceRating = the player's established
 * (peak, slowly decaying) rating for the anti-farming match factor.
 */
export interface CreateSessionResponse {
  sessionId: string;
  token: string;
  seed: number;
  startRating: number;
  referenceRating: number;
  rulesVersion: number;
  /** The pace this session was issued with — the client plays (and is replayed) at it. */
  relaxed: boolean;
}

export interface SubmitRunRequest {
  token: string;
  /** The engine's event log (RunSummary.events); the server replays it. */
  events: GameEvent[];
  /** Scores the client computed; must equal the replay exactly or the run is rejected. */
  claimed: { raw: number; rated: number };
  /** e.g. "web/0.1.0" — for diagnostics only. */
  clientVersion: string;
}

/**
 * Verdict on a run (200 for accepted and rejected runs alike).
 * - accepted: replay succeeded and no `reject` anomaly; the run is stored.
 * - flagged: accepted but hidden from leaderboards pending review.
 * - raw / rated / savedRating: the server's recomputed values (authoritative).
 *   For a rejected run savedRating is the unchanged stored rating (0 if none).
 * - ranks: the player's rank per scope (metric "rated", period "all") after
 *   this run — only scopes the player appears in (onboarded, opted in, not
 *   flagged); {} otherwise.
 * - personalBest: this run beat the player's previous best RatedScore or
 *   RawScore. Never true for flagged or placement runs.
 * - placement: this was the account's first completed run — stored, sets the
 *   level, but unranked (GAME_DESIGN § 5): ranks is {} and personalBest false.
 * - relaxed: the session was a "More time" run (GAME_DESIGN § 4.1) — stored and
 *   rated, never ranked: ranks is {} and personalBest false. Set on rejected
 *   runs too (it describes the session).
 * - reason: set when not accepted — an AnomalyCode ("score-mismatch", …),
 *   "session-used" / "session-expired" / "session-unknown", or (client-side
 *   only) "offline" / "network".
 */
export type SubmitRunResponse = SubmitResult;

// ───────────── Leaderboard ─────────────

/**
 * Query string of GET /api/leaderboard (see LeaderboardQuery). Semantics, which
 * OfflineDemoBackend implements identically (services/backend/leaderboard.ts):
 *
 * - Candidates: players who are onboarded AND opted in, with at least one
 *   accepted, unflagged run in the period. One row per player: their best run
 *   by `metric` (ties → the earlier run).
 * - period: "all" | "week" (runs in the last 7 × 24 h).
 * - scope narrows the board:
 *     global   — everyone
 *     country  — country
 *     province — province (and country, when known)
 *     city     — city, case-insensitive (and province/country, when known)
 *     school   — schoolId (query.schoolId, else the caller's school)
 *     grade    — grade (query.grade, else the caller's grade)
 *   The location (country/province/city) is taken from the query when it
 *   names any of the three, otherwise from the caller's profile — never mixed.
 *   SA provinces compare by code, cities case- and accent-insensitively.
 *   If the needed value is unknown (e.g. an anonymous caller asks for
 *   "school") the page is empty.
 * - grade (optional) additionally filters any scope ("Grade 7 in Gauteng").
 * - Order: score desc, then earlier run, then playerId. Ranks are competition
 *   ranks (equal scores share a rank: 1, 2, 2, 4).
 * - limit: default LEADERBOARD_LIMIT_DEFAULT, clamped to 1…LEADERBOARD_LIMIT_MAX.
 * - `me`: the caller's row whenever they are ranked on this board, even when
 *   outside the top `limit`; entries for the caller carry isMe = true.
 * - Entries never carry a player id (it is half of the credential) or a
 *   precise time: `entryKey` is an opaque, stable row key and `at` is the
 *   run's UTC day (YYYY-MM-DD).
 * - total: number of ranked players on the board.
 */
export interface LeaderboardSearch {
  metric: LeaderboardMetric;
  scope: LeaderboardScope;
  period: LeaderboardPeriod;
  grade?: string;
  country?: string;
  province?: string;
  city?: string;
  schoolId?: string;
  limit?: string;
}

export type LeaderboardResponse = LeaderboardPage;

export const LEADERBOARD_LIMIT_DEFAULT = 50;
export const LEADERBOARD_LIMIT_MAX = 100;
export const LEADERBOARD_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const LEADERBOARD_SCOPES: readonly LeaderboardScope[] = ['global', 'country', 'province', 'city', 'school', 'grade'];
export const LEADERBOARD_METRICS: readonly LeaderboardMetric[] = ['rated', 'raw'];
export const LEADERBOARD_PERIODS: readonly LeaderboardPeriod[] = ['all', 'week'];

/** Serialise a LeaderboardQuery to the query string the server expects (unset values omitted). */
export function leaderboardSearchParams(q: LeaderboardQuery): URLSearchParams {
  const params = new URLSearchParams({ metric: q.metric, scope: q.scope, period: q.period });
  if (q.grade !== undefined && q.grade !== null) params.set('grade', String(q.grade));
  if (q.country) params.set('country', q.country);
  if (q.province) params.set('province', q.province);
  if (q.city) params.set('city', q.city);
  if (q.schoolId) params.set('schoolId', q.schoolId);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  return params;
}

/** Clamp a requested page size. */
export function clampLeaderboardLimit(limit: number | undefined): number {
  if (limit === undefined || !Number.isFinite(limit)) return LEADERBOARD_LIMIT_DEFAULT;
  return Math.min(LEADERBOARD_LIMIT_MAX, Math.max(1, Math.floor(limit)));
}
